import { AccordionSummary, Chip, Grid, IconButton, Tooltip, Typography } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import CachedIcon from '@material-ui/icons/Cached';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import MaterialTable from 'material-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../../../services/api';
import useStyles from '../style';
import Loading from '../../../../components/Loading';
import DownloadLoadingPopup from '../../../../components/DownloadLoadingPopup';
import { toast } from 'react-toastify';
import {
  isFornecedorContratoMasterAssinado,
  normalizeContratosMastersAssinadosLista,
  resolveFornecedorContratoMasterStatus,
} from '../../../../utils/contratoMasterFornecedorStatus';

function StatusAdesaoChip({ rowData }) {
  const { key, label } = resolveFornecedorContratoMasterStatus(rowData);
  if (key === 'assinado') {
    return (
      <Chip
        size="small"
        label={label}
        style={{
          backgroundColor: '#e8f5e9',
          color: '#2e7d32',
          fontSize: '13px',
          fontWeight: 500,
          height: 24,
        }}
      />
    );
  }
  if (key === 'pendente') {
    return (
      <Chip
        size="small"
        label={label}
        style={{
          backgroundColor: '#fff3e0',
          color: '#e65100',
          fontSize: '13px',
          fontWeight: 500,
          height: 24,
        }}
      />
    );
  }
  return <span style={{ fontSize: '13px' }}>{label}</span>;
}

function bufferLooksLikePdf(arrayBuffer) {
  if (!arrayBuffer || arrayBuffer.byteLength < 4) return false;
  const u8 = new Uint8Array(arrayBuffer, 0, 4);
  return u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46;
}

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') return null;
  const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replaceAll(/['"]/g, ''));
    } catch {
      return utf8[1].trim().replaceAll(/['"]/g, '');
    }
  }
  const simple = /filename="([^"]+)"/i.exec(header) || /filename=([^;\n]+)/i.exec(header);
  if (simple?.[1]) return simple[1].trim().replaceAll(/['"]/g, '');
  return null;
}

function toastFirstApiMessage(json) {
  const r = json?.resposta;
  if (Array.isArray(r) && r[0]?.message) {
    toast.error(String(r[0].message));
    return;
  }
  if (json?.notifications?.[0]?.mensagem) {
    toast.error(String(json.notifications[0].mensagem));
    return;
  }
  toast.error('Não foi possível baixar o PDF.');
}

async function downloadPdfFromApiResponse(response, fallbackName) {
  const buffer = response?.data;
  if (!(buffer instanceof ArrayBuffer)) {
    toast.error('Resposta inválida do servidor ao baixar o PDF.');
    return;
  }

  const ct = String(response.headers?.['content-type'] || '').toLowerCase();
  const firstByte = buffer.byteLength ? new Uint8Array(buffer, 0, 1)[0] : 0;

  if (ct.includes('application/json') || firstByte === 0x7b) {
    try {
      const text = new TextDecoder('utf-8').decode(buffer);
      const json = JSON.parse(text);
      toastFirstApiMessage(json);
    } catch {
      toast.error('Não foi possível baixar o PDF.');
    }
    return;
  }

  if (!bufferLooksLikePdf(buffer)) {
    toast.error('O arquivo recebido não é um PDF válido.');
    return;
  }

  const fileName =
    response.headers?.['x-download-file-name'] ||
    response.headers?.['X-Download-FileName'] ||
    parseFilenameFromContentDisposition(response.headers?.['content-disposition']) ||
    fallbackName ||
    'contrato-master.pdf';

  const blob = new Blob([buffer], { type: 'application/pdf' });
  const url = globalThis.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  globalThis.URL.revokeObjectURL(url);
}

export function ContractMasterAssGroup({ providerId, fornecedorId, razaoSocialFornecedor }) {
  const classes = useStyles();
  const [contracts, setContracts] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [downloadingFcmId, setDownloadingFcmId] = useState(null);

  const fetchContracts = useCallback(async () => {
    if (!fornecedorId && !providerId) return;

    setLoading(true);
    try {
      const id = fornecedorId || providerId;
      const res = await api.get('OperacoesAntecipacoes/ConsultarContratosMastersAssinados', {
        params: { fornecedorId: id, ancoraId: 0, antecipacaoId: 0 },
      });

      if (res?.data?.status === 'Sucesso') {
        const lista = normalizeContratosMastersAssinadosLista(res.data.resposta);
        const nome = razaoSocialFornecedor || '—';
        setContracts(
          lista.map((row) => {
            const st = resolveFornecedorContratoMasterStatus(row);
            return {
              ...row,
              fornecedorRazaoSocial: nome,
              statusAdesaoLabel: st.label,
            };
          }),
        );
      } else {
        setContracts([]);
      }
    } catch (error) {
      setContracts([]);
      toast.error('Não foi possível carregar os contratos Master assinados.');
    } finally {
      setLoading(false);
    }
  }, [fornecedorId, providerId, razaoSocialFornecedor]);

  const handleDownload = async (rowData) => {
    const fcmid = rowData?.fornecedorContratoMasterId ?? rowData?.FornecedorContratoMasterId;
    setDownloadingFcmId(fcmid || null);
    try {
      const response = await api.get('OperacoesAntecipacoes/BaixarPdfContratoMaster', {
        params: {
          ancoraId: 0,
          antecipacaoId: 0,
          fornecedorId: fornecedorId || providerId,
          fornecedorContratoMasterId: fcmid,
          id: fcmid,
          envelopeClicksignId: rowData?.envelopeClicksignId || '',
        },
        responseType: 'arraybuffer',
        skipGlobalErrorHandler: true,
      });

      await downloadPdfFromApiResponse(response, null);
    } catch (error) {
      const data = error?.response?.data;
      if (data instanceof ArrayBuffer && data.byteLength && new Uint8Array(data, 0, 1)[0] === 0x7b) {
        try {
          const json = JSON.parse(new TextDecoder('utf-8').decode(data));
          toastFirstApiMessage(json);
        } catch {
          toast.error('Não foi possível baixar o PDF do contrato Master.');
        }
      } else {
        toast.error('Não foi possível baixar o PDF do contrato Master.');
      }
    } finally {
      setDownloadingFcmId(null);
    }
  };

  const handleVerifyStatus = async (rowData) => {
    try {
      const fcmid = rowData?.fornecedorContratoMasterId ?? rowData?.FornecedorContratoMasterId;
      const response = await api.get('OperacoesAntecipacoes/VerificarStatusClicksignContratoMaster', {
        params: {
          ancoraId: 0,
          antecipacaoId: 0,
          fornecedorId: fornecedorId || providerId,
          fornecedorContratoMasterId: fcmid,
          id: fcmid,
          envelopeClicksignId: rowData?.envelopeClicksignId || '',
        },
      });

      if (response?.data?.status === 'Sucesso') {
        toast.success(response?.data?.resposta?.message || 'Status atualizado com sucesso.');
        await fetchContracts();
      }
    } catch (error) {
      toast.error('Não foi possível verificar o status do Clicksign.');
    }
  };

  const memoizedTable = useMemo(
    () => (
      <MaterialTable
        localization={{
          body: {
            emptyDataSourceMessage: <span></span>,
          },
        }}
        className={classes.table}
        title="Multiple Actions Preview"
        options={{
          showTitle: false,
          search: false,
          toolbar: false,
          headerStyle: {
            backgroundColor: '#F5F5F5',
            textTransform: 'none',
            fontSize: '15px',
          },
          filtering: true,
          paging: false,
        }}
        columns={[
          {
            title: 'Fornecedor',
            field: 'fornecedorRazaoSocial',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
            },
          },
          {
            title: 'Destino de Pagamento',
            field: 'destinoPagamento',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
            },
          },
          {
            title: 'Nome do contrato',
            field: 'nomeContrato',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
            },
            render: (rowData) => rowData?.nomeContratoAssinado || rowData?.nomeContrato || '',
          },
          {
            title: 'Data da assinatura',
            field: 'dataAssinatura',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
            },
            render: (rowData) =>
              rowData?.dataFinalizacaoAssinatura ||
              rowData?.dataFimAssinatura ||
              rowData?.dataAssinatura ||
              '',
          },
          {
            title: 'Adesão (contrato master)',
            field: 'statusAdesaoLabel',
            cellStyle: {
              fontSize: '13px',
              padding: '8px 10px',
            },
            render: (rowData) => <StatusAdesaoChip rowData={rowData} />,
          },
          {
            title: 'Ações',
            field: 'actions',
            headerStyle: {
              textAlign: 'center',
            },
            cellStyle: {
              fontSize: '13px',
              textAlign: 'center',
              padding: '8px 16px',
            },
            filtering: false,
            sorting: false,
            disableClick: true,
            render: (rowData) => {
              const fcmid = rowData?.fornecedorContratoMasterId ?? rowData?.FornecedorContratoMasterId;
              const isDownloading = downloadingFcmId === fcmid;
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Tooltip placement="top" title="Baixar PDF">
                  <button
                    type="button"
                    className="pointer"
                    disabled={isDownloading}
                    onClick={() => handleDownload(rowData)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      opacity: isDownloading ? 0.65 : 1,
                      cursor: isDownloading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isDownloading ? (
                      <HourglassEmptyIcon style={{ fontSize: 30, color: '#FB8832' }} />
                    ) : (
                      <CloudDownloadIcon style={{ fontSize: 30, color: '#4AAF05' }} />
                    )}
                  </button>
                </Tooltip>
                {!isFornecedorContratoMasterAssinado(rowData) && (
                  <Tooltip placement="top" title="Verificar status Clicksign">
                    <button
                      type="button"
                      className="pointer"
                      disabled={isDownloading}
                      onClick={() => handleVerifyStatus(rowData)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        opacity: isDownloading ? 0.5 : 1,
                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <HourglassEmptyIcon style={{ fontSize: 30, color: '#FB8832' }} />
                    </button>
                  </Tooltip>
                )}
              </span>
              );
            },
          },
        ]}
        data={contracts}
      />
    ),
    [contracts, classes.table, downloadingFcmId],
  );

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.summary}>
          <Typography className={classes.heading}>Contratos Master assinados</Typography>
        </AccordionSummary>

        <AccordionDetails className={classes.details}>
          <Grid container>
            <Grid item xs={12} className={classes.registerBtn}>
              <Tooltip placement="top" title="Recarregar conteúdo">
                <IconButton
                  aria-label="Recarregar contratos master assinados"
                  onClick={() => fetchContracts()}
                  size="small"
                >
                  <CachedIcon className="pointer" style={{ fontSize: 28, color: '#026894' }} />
                </IconButton>
              </Tooltip>
            </Grid>
            {isLoading ? (
              <Loading />
            ) : (
              <Grid item xs={12}>
                {memoizedTable}
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      <DownloadLoadingPopup
        open={Boolean(downloadingFcmId)}
        message="Baixando contrato master assinado..."
      />
    </>
  );
}
